<template>
  <div class="questions-library">
    <div class="mb-2 mb-md-6">
      <VRow class="mb-2 mb-md-6">
        <VCol cols="12" lg="6">
          <VTextField
            :label="$t('search')"
            append-icon="mdi-magnify"
            class="white"
            hide-details
            clearable
            outlined
            dense
            autocomplete="off"
            @input="filterQuestions"
          />
        </VCol>
        <VCol
          v-if="category"
          cols="12"
          class="d-md-none"
        >
          <VTextField
            :value="category"
            readonly
            outlined
            dense
            hide-details
            append-icon="mdi-format-list-bulleted"
            class="presets-category mb-3 font-weight-300"
            @click="category = null"
          />
        </VCol>
      </VRow>
      <VRow>
        <template v-if="filteredCategories.length">
          <VCol
            v-if="$vuetify.breakpoint.mdAndUp || !category"
            cols="12"
            md="4"
            class="py-0"
          >
            <VTreeview
              :items="filteredCategories"
              activatable
              return-object
              dense
              hoverable
              class="v-height huntica-nice-scroll tree-view"
              :class="{ 'presets-mobile py-4': !$vuetify.breakpoint.mdAndUp}"
              @update:active="setCategoryQuestions"
            />
          </VCol>
        </template>
        <template v-if="filteredQuestions.length">
          <VCol
            v-if="$vuetify.breakpoint.mdAndUp || category || search"
            cols="12"
            md="8"
            class="py-0 pl-md-2"
          >
            <VList dense class="v-height py-0">
              <VacancyQuestionsGroup
                :questions="visibleQuestions"
                :selected="selected"
                @update="selectQuestions"
              />
            </VList>
          </VCol>
        </template>
        <VCol v-else class="not-found text-center flex-column py-0">
          <p class="mb-1">
            {{ $t('questionNotFound') }}?
            <br class="d-md-none">
            <span>
              {{ $t('freeFormQuestion') }}
            </span>
          </p>
          <p>
            {{ $t('textQuestionTip') }}
          </p>
        </VCol>
      </VRow>
    </div>
    <div v-if="!showTextQuestionForm" class="d-flex pb-2 pb-md-0">
      <a class="d-none d-md-block link-dotted" @click="toggle">
        {{ $t('notFoundQuestion') }}
      </a>
    </div>
    <VCard
      v-if="!showTextQuestionForm"
      tile
      class="white text-center grey--text d-md-none pa-4 py-3 mb-2 mt-n2"
      elevation="0"
      @click="toggle"
    >
      {{ $t('enterQuestionManually') }}
    </VCard>
    <div v-if="$vuetify.breakpoint.mdAndUp && showTextQuestionForm">
      <transition v-if="showTextQuestionForm" :name="transition" mode="out-in">
        <VacancyQuestionsLibraryText
          ref="addTextQuestion"
          class="mb-6"
          :show-preview="showPreview"
          :is-ai-questions="isAiQuestions"
          @input="addTextQuestion"
          @save="$emit('save')"
        />
      </transition>
      <VacancyQuestionsLibraryTextList
        @destroy="destroyQuestion"
        @update="updateQuestion"
        :questions="textQuestions"
        :show-preview="showPreview"
      />
    </div>
    <VacancyQuestionsLibraryTextList
      v-if="$vuetify.breakpoint.smAndDown"
      class="pb-2"
      @destroy="destroyQuestion"
      @update="updateQuestion"
      :questions="textQuestions"
      :show-preview="showPreview"
    />
    <VDialog v-if="$vuetify.breakpoint.smAndDown" v-model="showTextQuestionForm" fullscreen>
      <VCard class="grey lighten-5 pb-2 pb-md-4">
        <div class="white pa-4">
          <VBtn
            fab
            icon
            small
            absolute
            top
            right
            @click="toggle"
            class="mt-6 mr-n4"
          >
            <VIcon>
              mdi-close
            </VIcon>
          </VBtn>
          <div class="step-title px-0 mb-n2">
            {{ $t('AddQuestionTitle') }}
          </div>
        </div>
        <VCardText class="px-4 mt-4 pb-2 pb-md-4">
          <VacancyQuestionsLibraryText
            :show-preview="showPreview"
            :is-ai-questions="isAiQuestions"
            @input="addTextQuestion"
            @save="$emit('save')"
          />
          <VacancyQuestionsLibraryTextList
            @destroy="destroyQuestion"
            @update="updateQuestion"
            :questions="textQuestions"
            :show-preview="showPreview"
          />
        </VCardText>
        <div class="d-flex white pa-2">
          <VBtn
            class="mx-auto"
            color="success"
            @click="toggle"
            elevation="0"
          >
            {{ $t('close') }}
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<script>
import escapeRegExp from 'lodash.escaperegexp';
import VacancyQuestionsLibraryText from '@/components/Vacancy/VacancyQuestionsLibraryText';
import VacancyQuestionsLibraryTextList from '@/components/Vacancy/VacancyQuestionsLibraryTextList';
import { questionTypes } from '@/enums';
import { extractQuestions } from '@/helpers/questions';
import ValidationMixin from '../../mixins/ValidationMixin';
import VacancyQuestionsGroup from './VacancyQuestionsGroup.vue';

export default {
  name: 'VacancyQuestionsLibrary',
  components: {
    VacancyQuestionsLibraryTextList,
    VacancyQuestionsLibraryText,
    VacancyQuestionsGroup,
  },
  mixins: [ValidationMixin],
  model: {
    prop: 'selected',
    event: 'update',
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    selected: {
      type: Array,
      required: true,
    },
    showPreview: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAiQuestions: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      transition: 'slide-x-transition',
      category: null,
      categoryQuestions: [],
      filteredQuestions: [...this.questions],
      search: '',
      showTextQuestionForm: false,
    };
  },
  computed: {
    visibleQuestions() {
      if (!this.categoryQuestions.length) {
        return this.filteredQuestions;
      }

      return this.filteredQuestions.filter((question) => {
        return this.categoryQuestions.some((q) => question.questionId === q.id);
      });
    },
    filteredCategories() {
      if (this.search !== '') {
        return this.categories.filter((category) => {
          return category.questions.length > 0 && category.questions.some(
            (q) => this.filteredQuestions.some((question) => q.id === question.questionId),
          );
        });
      }

      return this.categories.filter((category) => category.questions.length > 0) || [];
    },
    textQuestions() {
      return this.selected.filter((question) => question.type === questionTypes.TEXT);
    },
  },
  watch: {
    questions() {
      this.filteredQuestions = [...this.questions];
    },
  },
  methods: {
    toggle() {
      this.showTextQuestionForm = !this.showTextQuestionForm;
      if (this.showTextQuestionForm && this.$vuetify.breakpoint.mdAndUp) {
        this.$nextTick(() => {
          this.$vuetify.goTo(this.$refs.addTextQuestion, {
            container: '.v-dialog',
          });
        });
      }
    },
    setCategoryQuestions([categoryObject]) {
      if (!categoryObject) {
        this.categoryQuestions = [];
        this.category = null;
        return;
      }
      this.categoryQuestions = extractQuestions(categoryObject);
      this.category = categoryObject.name;
    },
    filterQuestions(text) {
      this.search = text;
      if (text === null || text === '' || text === undefined) {
        this.filteredQuestions = [...this.questions];
      } else {
        const regExp = new RegExp(escapeRegExp(text), 'gmiu');
        this.filteredQuestions = this.questions.filter((q) => regExp.test(q.question));
      }
    },
    async addTextQuestion(question) {
      this.$emit('update', [...this.selected, question]);
    },
    selectQuestions(questions) {
      this.$emit('update', questions);
    },
    updateQuestion(question) {
      this.$emit('update', this.selected
        .map((q) => (q.type !== question.type || q.id !== question.id ? q : question)));
    },
    destroyQuestion(question) {
      this.$emit('update', this.selected
        .filter((q) => q.type !== question.type || q.id !== question.id));
    },
  },
};
</script>

<style lang="scss" scoped>
.questions-library .not-found {
  height: 400px;
}

.v-height {
  max-height: 400px;
}

.tree-view {
  &::v-deep {
    .v-treeview-node__content {
      cursor: pointer;
    }
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .tree-view {
    overflow: initial;
    max-height: none !important;
  }
  .questions-library .not-found {
    height: auto;
  }
  .v-height {
    max-height: calc(100vh - 397px);
    overflow: hidden;
  }
}

  .presets-mobile {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    font-weight: 300;
    font-size: 16px;
    white-space: normal;
  }
</style>

<style lang="scss">
.questions-library .v-list-item {
  background: white;
}
</style>

<i18n locale="en" lang="yaml">
  questionLibrary: Questions library
  ownQuestion: Custom question
  search: Search
  all: All
  questionNotFound: Did not find question
  notFoundQuestion: Did not find appropriate question in the library? Ask custom question.
  enterQuestionManually: Enter question manually
  freeFormQuestion: Ask custom question
  AddQuestionTitle: Add a custom question
  textQuestionTip: he question will not be read out by the actor, but will appear on the screen
  freeFormQuestion2: Ask custom question
  charactersLimit: The maximum number of characters is 200
  textQuestionTip2: We recommend including no more than 1-2 of these kind of question in the interview
  addQuestion: Add question
  questionAdded: Question added
  enterQuestion: Enter question text
  save: Save
  close: Close
</i18n>

<i18n locale="ru" lang="yaml">
  questionLibrary: Библиотека вопросов
  ownQuestion: Свой вопрос
  search: Введите ключевые слова
  all: Все
  questionNotFound: Не нашли вопрос
  notFoundQuestion: Не нашли нужный вопрос в библиотеке? Введите его вручную
  enterQuestionManually: Ввести вопрос вручную
  freeFormQuestion: Задайте его в свободной форме
  AddQuestionTitle: Добавление вопроса вручную
  textQuestionTip: Вопрос, введенный вручную, будет показан текстом на экране, а не озвучен диктором
  freeFormQuestion2: Задайте свой вопрос в свободной форме
  charactersLimit: Максимальное количество символов - 200
  textQuestionTip2: Мы рекомендуем включать не более 1-2 таких вопросов в интервью
  addQuestion: Добавить вопрос
  questionAdded: Вопрос добавлен
  enterQuestion: Добавьте текст вопроса
  save: Сохранить
  close: Закрыть
</i18n>
